/**
 * The basket being built, and the one button that sends it — ROADMAP 9.66.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A WORKING COPY, WITH THE SERVER AS THE AUTHORITY
 *
 * `legs` and `name` are edited at keystroke speed and are not written through
 * on every change. The `Basket` row is the authority, and every mutation
 * stores the server's ANSWER back rather than the body it sent: the sequence,
 * and so the default name, are assigned server-side, and a leg's facts come
 * off the instrument catalogue rather than off whatever the ladder happened
 * to be showing.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * CLOSING SAVES — ROADMAP 9.67
 *
 * A basket assembled and then abandoned at the cross used to vanish, which
 * is the one thing a draft exists to prevent: the owner's requirement was
 * that a basket survives being closed "whether it was placed or never placed
 * and left in between".
 *
 * It fires on UNMOUNT, and EVERYTHING it reads — including the save function
 * itself — comes out of a ref rather than its dependency list. An unmount
 * effect whose teardown performs the work cannot depend on anything that
 * changes between renders: the teardown would then fire on every render
 * rather than on the last one. See the effect for what that cost.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SAVE, THEN PLACE — IN THAT ORDER, ALWAYS
 *
 * `POST /baskets/:id/place` sends what the ROW holds, not what the request
 * carries. That is what lets the basket's name reach `Trade.label`, which
 * `openTrade` will not accept from a browser — the name comes off a value the
 * server owns.
 *
 * It also means an unsaved edit is an edit that would not be sent. So Place
 * saves first, and a failed save stops the placement rather than letting it
 * go with the previous legs. The coupling is deliberate: the alternative is a
 * button that sometimes sends something other than what is on screen.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { addToBasket, removeFromBasket, setBasketLots } from '@apna/shared'
import { api } from '@/lib/api'
import { notifyFail } from '@/components/Toast'
import { tickSocket } from '@/feed/tickSocket'
import type { OrderAccount } from '@/screens/terminal/placeOrder'
import type { BasketDto, BasketLegDto } from '@/types/basket'
import type { BasketSeed } from '../basketSeed'
import type { BasketsApi } from './useBaskets'

export interface BasketDraft {
  /** The saved row, or null while the trader is still choosing an underlying. */
  basket: BasketDto | null
  legs: readonly BasketLegDto[]
  name: string
  setName: (name: string) => void
  /** Why the last action was refused. Null when nothing is wrong. */
  error: string | null
  starting: boolean
  placing: boolean
  saving: boolean
  /** True while the last save still reflects what is on screen. */
  saved: boolean

  /** Create a basket on this underlying and open it. */
  start: (underlying: string) => void
  /**
   * What the checklist says right now — 9.69.
   *
   * `ready` gates the place button; `done` is what gets RECORDED, and the
   * two differ because "Skip for this entry" unlocks without confirming.
   */
  setChecklist: (
    ready: boolean,
    done: boolean,
    result: readonly { rule: string; ticked: boolean }[],
  ) => void
  /** False while the basket's checklist is neither ticked nor skipped. */
  checklistReady: boolean
  /** Load a saved draft into the working copy. */
  load: (row: BasketDto) => void
  /** Create and open a basket on a strategy's resolved legs — 9.69. */
  openSeed: (seed: BasketSeed) => void
  /**
   * A contract the trader clicked, already described by the ladder that drew
   * it. NETS rather than appending — see `addToBasket`.
   */
  add: (leg: BasketLegDto) => void
  resize: (token: number, lots: number) => void
  drop: (token: number) => void
  /** Keep it, without sending it. */
  save: () => void
  place: () => void
}

export interface BasketDraftInput {
  baskets: BasketsApi
  connection: OrderAccount | null
  onPlaced: () => void
  onDone: () => void
}

/**
 * IT KNOWS NOTHING ABOUT THE LADDER, deliberately.
 *
 * `add` takes a leg the chain has already described — its expiry and lot size
 * are facts of the contract that was clicked, and the component that drew the
 * row is the one holding them. Reading them out of a ladder here would make
 * this hook depend on the ladder and the ladder depend on this hook's chosen
 * underlying, which is a cycle React resolves by lagging a render.
 */
export function useBasketDraft({
  baskets,
  connection,
  onPlaced,
  onDone,
}: BasketDraftInput): BasketDraft {
  const [basket, setBasket] = useState<BasketDto | null>(null)
  const [legs, setLegs] = useState<readonly BasketLegDto[]>([])
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [starting, setStarting] = useState(false)
  const [placing, setPlacing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(true)
  /*
   * ── THE CHECKLIST, ASKED HERE AND NOT IN THE TICKET — ROADMAP 9.69 ─────
   *
   * The rules are claims about SPECIFIC STRIKES — "resistance above the
   * short strike is identified" — so a tick made before Adjustments
   * describes a structure the trader then changed. They are asked against
   * the legs actually being sent.
   *
   * Two values, not one, exactly as `StrategyPicker` keeps them: whether the
   * button may unlock (ticked OR deliberately skipped), and whether the
   * checks were actually COMPLETED, which is what gets recorded.
   */
  const [checklistReady, setChecklistReady] = useState(true)
  const [checklistDone, setChecklistDone] = useState(false)
  const [checklist, setChecklistResult] = useState<
    readonly { rule: string; ticked: boolean }[]
  >([])
  const setChecklist = useCallback(
    (
      ready: boolean,
      done: boolean,
      result: readonly { rule: string; ticked: boolean }[],
    ) => {
      setChecklistReady(ready)
      setChecklistDone(done)
      setChecklistResult(result)
    },
    [],
  )

  // A refusal belongs to the basket that produced it.
  useEffect(() => setError(null), [basket?.id])

  /*
   * WHAT THE UNMOUNT SAVE READS. Kept in a ref because the effect below must
   * not re-arm on every edit — see the header. Written on every render, so
   * it is always the latest, which is exactly what a closing save wants.
   */
  const latest = useRef({ basket, legs, name, saved: true, put: baskets.save })
  latest.current = { basket, legs, name, saved, put: baskets.save }

  const load = useCallback((row: BasketDto) => {
    setBasket(row)
    setLegs(row.legs)
    setName(row.name)
    setSaved(true)
    /*
     * A basket with no checklist has nothing to confirm, so the button is
     * unlocked. One WITH a checklist starts locked — the ticks are per
     * placement and must not be inherited from the last basket opened, the
     * same rule `PreTradeChecklist` applies across a close.
     */
    const asks = (row.checklist?.length ?? 0) > 0
    setChecklistReady(!asks)
    setChecklistDone(false)
    setChecklistResult([])
  }, [])

  /** Every edit makes the row stale, and the Save button says so. */
  const touch = useCallback(() => setSaved(false), [])

  const start = useCallback(
    (underlying: string) => {
      setStarting(true)
      void baskets
        .create(underlying)
        .then(load)
        .catch((e: unknown) =>
          setError(e instanceof Error ? e.message : 'The basket could not be started.'),
        )
        .finally(() => setStarting(false))
    },
    [baskets, load],
  )

  /**
   * Open on a strategy's resolved legs.
   *
   * The basket is created with them ALREADY IN IT rather than created empty
   * and then saved: an empty row would be a basket in the trader's drawer
   * for however long the second round trip took, and a failure between the
   * two would leave one there for good.
   */
  const openSeed = useCallback(
    (seed: BasketSeed) => {
      setStarting(true)
      void baskets
        .create(seed.underlying, {
          legs: seed.legs,
          name: seed.name,
          strategyId: seed.strategyId,
        })
        .then(load)
        .catch((e: unknown) =>
          setError(e instanceof Error ? e.message : 'The basket could not be opened.'),
        )
        .finally(() => setStarting(false))
    },
    [baskets, load],
  )

  /**
   * ONE LOT PER CLICK, and the stepper beside the leg is how it is re-sized.
   *
   * A header size would make the same click mean different things depending
   * on a control the trader is not looking at — which is what the ladder's
   * own B and S have to live with, and the reason a basket puts the size on
   * the row that owns it.
   */
  const add = useCallback(
    (leg: BasketLegDto) => {
      const next = addToBasket<BasketLegDto>(legs, leg)
      if (!next.ok) {
        setError(next.why)
        return
      }
      setError(null)
      setLegs(next.legs)
      touch()
    },
    [legs, touch],
  )

  const resize = useCallback(
    (token: number, lots: number) => {
      setLegs((prev) => setBasketLots(prev, token, lots))
      touch()
    },
    [touch],
  )
  const drop = useCallback(
    (token: number) => {
      setLegs((prev) => removeFromBasket(prev, token))
      touch()
    },
    [touch],
  )
  const rename = useCallback(
    (next: string) => {
      setName(next)
      touch()
    },
    [touch],
  )

  const save = useCallback(() => {
    const row = basket
    if (!row || saving || placing) return
    setSaving(true)
    setError(null)
    void baskets
      .save(row.id, name.trim() || row.name, legs)
      .then((next) => {
        setBasket(next)
        setLegs(next.legs)
        setName(next.name)
        setSaved(true)
      })
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : 'The basket could not be saved.'),
      )
      .finally(() => setSaving(false))
  }, [basket, legs, name, saving, placing, baskets])

  /*
   * ── THE CLOSING SAVE ───────────────────────────────────────────────────
   *
   * EMPTY DEPS, AND THE SAVE FUNCTION COMES OUT OF THE REF TOO.
   *
   * This must run exactly once, on unmount. `useBaskets` returns a fresh
   * object on every render, so depending on it — which the first cut did —
   * tears this effect down and re-arms it on every render, and the teardown
   * IS the save: a PUT per keystroke, each one updating the list, each
   * update re-rendering and firing it again. A save loop dressed as a
   * cleanup.
   *
   * So nothing this effect reads is in its dependency list. The ref is
   * written on every render and is therefore always current, which is
   * exactly what a closing save wants.
   *
   * It is fire-and-forget by necessity: the component is going away and
   * there is nobody left to show a failure to, so a toast is the whole of
   * the error handling. Nothing is SENT — a draft is written, which is what
   * the cross should always have done.
   */
  useEffect(
    () => () => {
      const { basket: row, legs: held, name: called, saved: clean, put } = latest.current
      if (!row || clean) return
      void put(row.id, called.trim() || row.name, held).catch((e: unknown) =>
        notifyFail(
          'Basket not saved',
          e instanceof Error ? e.message : 'The basket could not be saved.',
        ),
      )
    },
    [],
  )

  const place = useCallback(() => {
    if (!basket || !connection || legs.length === 0 || placing) return
    setPlacing(true)
    setError(null)
    void (async () => {
      try {
        const saved = await baskets.save(basket.id, name.trim() || basket.name, legs)
        setBasket(saved)
        setLegs(saved.legs)
        setName(saved.name)
        setSaved(true)

        await api.post(`/baskets/${saved.id}/place`, {
          simulationSessionId: tickSocket.getMarketContext().sessionId,
          connectionId: connection.id,
          /*
           * Recorded, not enforced — the server accepts an entry that
           * skipped the checks. What matters afterwards is which entries
           * were taken with the stop and which without.
           */
          checklistDone,
          ...(checklist.length > 0 ? { checklist } : {}),
          /*
           * ONE value per user ACTION, so a double click cannot become two
           * baskets: the whole send shares one idempotency key, exactly as a
           * straddle placed from the ladder does.
           */
          clientRequestId: `bsk-${saved.id}-${Date.now()}`,
        })
        /*
         * Silent on success, as the ladder's B and S are. Accepted is not
         * filled; the drawer this opens is where the fills land, and
         * `useOrderFailureToasts` speaks for the legs that fail, naming each.
         */
        onPlaced()
        onDone()
      } catch (e: unknown) {
        const why = e instanceof Error ? e.message : 'The basket could not be placed.'
        setError(why)
        notifyFail(`${name || 'Basket'} not placed`, why)
      } finally {
        setPlacing(false)
      }
    })()
  }, [
    basket,
    connection,
    legs,
    name,
    placing,
    baskets,
    checklistDone,
    checklist,
    onPlaced,
    onDone,
  ])

  return {
    basket,
    legs,
    name,
    setName: rename,
    error,
    starting,
    placing,
    saving,
    saved,
    start,
    load,
    openSeed,
    setChecklist,
    checklistReady,
    add,
    resize,
    drop,
    save,
    place,
  }
}
